'use client'

import { cn } from '@/utilities/ui'
import * as React from 'react'

/**
 * Wilson Property canonical Radio Group.
 *
 * Figma spec:
 *   Radio (2198:39221 / 2198:39223):
 *     size 16px, rounded-full, white bg, 1px theme/border #e0dfe0, shadow/xs.
 *     checked -> border width goes 1px → 2px, colour stays theme/border;
 *     only the 8px dot uses theme/foreground #000, centred (4px inset).
 *     focus -> theme/ring (#8cc64080) at 3px spread.
 *   Error (2198:39229 unchecked / 2198:39231 checked): border theme/destructive
 *     #ef4444 (1px / 2px) and the dot switches to #ef4444.
 *   Error focused (2198:39233 / 2198:39235): same, plus a 3px
 *     color/status/error-300 (#fca5a5) ring.
 *   Disabled (2198:39237 / 2198:39239): 50% opacity.
 *   Radio Group row (2198:39242): flex items-center gap-2.5 (10px).
 *   Option label (Body/bodySmAlt): Lato 400, 14/16 (text-body-sm), foreground.
 *
 * NOTE: color/status/error-300 (#fca5a5) has no entry in globals.css `@theme`
 * yet, so the error focus ring is the only literal hex here. Swap it for the
 * token once `--color-error-300` lands.
 *
 * Built on the native radio input (no extra dependency) so the dot + checked
 * border are driven purely by CSS state.
 */

type RadioGroupContextValue = {
  name: string
  value?: string
  onValueChange?: (value: string) => void
  invalid?: boolean
}

const RadioGroupContext = React.createContext<RadioGroupContextValue | null>(null)

const RadioGroup: React.FC<
  {
    name: string
    value?: string
    defaultValue?: string
    onValueChange?: (value: string) => void
    /** Renders every item in Figma's error state. */
    invalid?: boolean
    className?: string
    children?: React.ReactNode
  } & Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>
> = ({ name, value, defaultValue, onValueChange, invalid, className, children, ...props }) => {
  const [internal, setInternal] = React.useState(defaultValue)
  const current = value ?? internal

  const handleChange = React.useCallback(
    (next: string) => {
      if (value === undefined) setInternal(next)
      onValueChange?.(next)
    },
    [value, onValueChange],
  )

  return (
    <RadioGroupContext.Provider
      value={{ name, value: current, onValueChange: handleChange, invalid }}
    >
      {/* aria-invalid lives on the group — the radiogroup role supports it; the
          individual radio inputs don't (a11y audit), they style via data-invalid. */}
      <div
        role="radiogroup"
        aria-invalid={invalid || undefined}
        className={cn('flex flex-col gap-3', className)}
        {...props}
      >
        {children}
      </div>
    </RadioGroupContext.Provider>
  )
}

const RadioGroupItem: React.FC<{
  value: string
  id?: string
  label?: React.ReactNode
  disabled?: boolean
  /** Renders this item in Figma's error state. Falls back to the group value. */
  invalid?: boolean
  className?: string
}> = ({ value, id, label, disabled, invalid, className }) => {
  const ctx = React.useContext(RadioGroupContext)
  const checked = ctx?.value === value
  const inputId = id ?? `${ctx?.name ?? 'radio'}-${value}`
  const isInvalid = invalid ?? ctx?.invalid ?? false

  return (
    <label
      htmlFor={inputId}
      className={cn(
        'flex items-center gap-2.5 cursor-pointer',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      <span className="relative inline-flex size-4 shrink-0">
        <input
          type="radio"
          id={inputId}
          name={ctx?.name}
          value={value}
          checked={checked}
          disabled={disabled}
          data-invalid={isInvalid || undefined}
          onChange={() => ctx?.onValueChange?.(value)}
          className="peer absolute inset-0 size-4 cursor-[inherit] appearance-none rounded-full border border-border bg-white shadow-xs outline-none transition-[border-color,box-shadow] checked:border-2 focus-visible:ring-[3px] focus-visible:ring-ring data-invalid:border-destructive data-invalid:focus-visible:ring-[#fca5a5]"
        />
        {/* 8px dot, centred (4px inset within the 16px box) */}
        <span
          className={cn(
            'pointer-events-none absolute left-1 top-1 size-2 rounded-full opacity-0 peer-checked:opacity-100',
            isInvalid ? 'bg-destructive' : 'bg-foreground',
          )}
        />
      </span>
      {label != null && <span className="text-body-sm font-normal text-foreground">{label}</span>}
    </label>
  )
}

export { RadioGroup, RadioGroupItem }
